// src/components/ReviewMissedQuestionsPage.tsx
"use client";

import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import {
    Box, Button, Typography, CircularProgress, useTheme, Paper, Divider, Chip, Tabs, Tab,
    Card, CardContent, CardHeader, Stack, TextField, Alert, FormControl, FormLabel,
    RadioGroup, FormControlLabel, Radio, Select, MenuItem, InputLabel, LinearProgress,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon, SkipNext as SkipNextIcon, SkipPrevious as SkipPreviousIcon,
    Visibility as VisibilityIcon, ErrorOutline as ErrorOutlineIcon,
    CheckCircleOutline as CheckCircleOutlineIcon, Replay as ReplayIcon,
    EventRepeat as EventRepeatIcon, DoneAll as DoneAllIcon, Check as CheckIcon,
    EditNote as EditNoteIcon, MenuBook as BookOpen, School as SchoolIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from "framer-motion";
import {
    UserContext, SkillName, TestAttempt, StudyPlanData, UserData,
    WeeklyProgressUpdate, applyWeeklyProgressToStudyPlan,
} from './UserAndProfile/UserContext';
import { PromptContext } from './PromptContext';
import ChatComponent from './ChatComponent';
import PersonalizedBar from './PersonalizedBar';
import { useNavigate, useLocation } from 'react-router-dom';
import { TestQuestion, UserAnswerRecord } from '../types';
import { getCurrentWeek } from './StudyPlan/studyPlanUtils';

// --- Types ---

type ReviewStatus =
    | 'Hard - Review Next Time'
    | 'Somewhat Hard - Review in 3 Days'
    | 'Good - Review in 14 Days'
    | 'Mastered! - No Review';

export interface MissedQuestionReviewItem extends UserAnswerRecord {
    originalTestId: string;
    originalTestType: 'diagnostic' | 'full';
    originalTestNumber?: number;
    originalSectionIndex: number;
    questionText: string;
    choices: string[];
    explanation: string;
    fullCorrectAnswerText: string;
    pictureURL?: string | null;
}

interface ReviewLocationState {
    action?: 'viewRecent' | 'startRecentReview' | 'reviewSpecific';
    testId?: string;
    targetQuestionIndex?: number;
    targetSectionIndex?: number;
}

type ComfortOption = { value: ReviewStatus; label: string; subtitle: string };

const COMFORT_OPTIONS: ComfortOption[] = [
    { value: 'Hard - Review Next Time', label: 'Hard', subtitle: 'Review tomorrow' },
    { value: 'Somewhat Hard - Review in 3 Days', label: 'Medium', subtitle: 'Review in 3 days' },
    { value: 'Good - Review in 14 Days', label: 'Easy', subtitle: 'Review in 2 weeks' },
    { value: 'Mastered! - No Review', label: 'Mastered', subtitle: 'No more reviews' },
];

const DEFAULT_COMFORT: ReviewStatus = 'Good - Review in 14 Days';

// --- Helpers ---

const calculateNextReviewDate = (status: ReviewStatus, currentDate: number): number | null => {
    const oneDay = 24 * 60 * 60 * 1000;
    switch (status) {
        case 'Hard - Review Next Time': return currentDate + oneDay;
        case 'Somewhat Hard - Review in 3 Days': return currentDate + 3 * oneDay;
        case 'Good - Review in 14 Days': return currentDate + 14 * oneDay;
        case 'Mastered! - No Review': return null;
        default: return null;
    }
};

/** Maps attempt IDs to question-bank JSON paths (including demo diagnostics). */
const getTestJsonPath = (testId: string): string | null => {
    if (testId.startsWith('demo-diagnostic')) {
        return '/satquestions/demo-diagnostic-test.json';
    }
    if (testId.startsWith('diagnostic-')) {
        return '/satquestions/diagnostic-test.json';
    }
    if (testId.startsWith('full-')) {
        const parts = testId.split('-');
        // full-test-N-timestamp → parts[2] is N
        if (parts.length >= 3 && !isNaN(parseInt(parts[2], 10))) {
            return `/satquestions/full-test-${parts[2]}.json`;
        }
    }
    console.error('Could not determine JSON path for testId:', testId);
    return null;
};

const extractCorrectAnswerText = (fullQuestion: TestQuestion): string => {
    let fullCorrectAnswerText = fullQuestion.answer || '';
    if (fullQuestion.choices && fullQuestion.answer) {
        const correctLetter = fullQuestion.answer.charAt(0).toUpperCase();
        if (correctLetter >= 'A' && correctLetter <= 'Z') {
            const choiceIndex = correctLetter.charCodeAt(0) - 'A'.charCodeAt(0);
            if (choiceIndex >= 0 && choiceIndex < fullQuestion.choices.length) {
                fullCorrectAnswerText = fullQuestion.choices[choiceIndex].replace(/^[A-Z]\)\s*/, '');
            }
        }
    }
    return fullCorrectAnswerText;
};

const buildReviewItem = (
    question: UserAnswerRecord,
    fullQuestion: TestQuestion,
    attempt: TestAttempt,
    sectionIndex: number,
): MissedQuestionReviewItem => ({
    ...question,
    skill: (fullQuestion.skill || question.skill) as SkillName,
    difficulty: (fullQuestion.difficulty || question.difficulty) as 'Easy' | 'Medium' | 'Hard',
    originalTestId: attempt.id,
    originalTestType: attempt.id.startsWith('diagnostic') || attempt.id.startsWith('demo-diagnostic')
        ? 'diagnostic'
        : 'full',
    originalTestNumber: attempt.id.startsWith('full-')
        ? parseInt(attempt.id.split('-')[2], 10)
        : undefined,
    originalSectionIndex: sectionIndex,
    questionText: fullQuestion.question || '',
    choices: fullQuestion.choices || [],
    explanation: fullQuestion.explanation || 'No explanation available.',
    fullCorrectAnswerText: extractCorrectAnswerText(fullQuestion),
    pictureURL: fullQuestion.pictureURL || null,
});

const shortComfortLabel = (status: ReviewStatus): string =>
    COMFORT_OPTIONS.find(o => o.value === status)?.label ?? status;

// --- Component ---

const ReviewMissedQuestionsPage: React.FC = () => {
    const { user, updateUserData } = useContext(UserContext);
    const {
        renderLatexOrText, setQuestionText, setChoices, setCorrectAnswer, setInteractionStage,
        setExplanationText, setSelectedSkill, setSelectedDifficulty, clearChat, setPictureURL,
    } = useContext(PromptContext)!;
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [allMissedQuestions, setAllMissedQuestions] = useState<MissedQuestionReviewItem[]>([]);
    const [viewMode, setViewMode] = useState<'spacedRepetition' | 'recentTest' | 'all'>('spacedRepetition');
    const [activeFilters, setActiveFilters] = useState<{ testType?: string; skill?: SkillName; difficulty?: string }>({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number | null>(null);
    const [reflectionInput, setReflectionInput] = useState('');
    const [reviewMode, setReviewMode] = useState<'practice' | 'explanation'>('practice');
    const [currentSelection, setCurrentSelection] = useState('');
    const [freeResponseInput, setFreeResponseInput] = useState('');
    const [isPracticeSubmitted, setIsPracticeSubmitted] = useState(false);
    const [selectedComfortLevel, setSelectedComfortLevel] = useState<ReviewStatus | null>(null);
    const [showCompletionSummary, setShowCompletionSummary] = useState(false);
    const [sessionReviewedCount, setSessionReviewedCount] = useState(0);
    const [saving, setSaving] = useState(false);

    const testDataCache = useRef<Record<string, any>>({});
    const [initialContextTestId, setInitialContextTestId] = useState<string | null>(null);
    const [actionProcessed, setActionProcessed] = useState(false);
    const initialActionDetailsRef = useRef<{
        action: 'viewRecent' | 'startRecentReview' | 'reviewSpecific';
        testId: string;
        targetQuestionIndex?: number;
        targetSectionIndex?: number;
    } | null>(null);
    // Prevents re-seeding PromptContext on every filter recompute for the same index
    const seededIndexRef = useRef<number | null>(null);
    // After save, advance to this item by identity (avoids wrong index when Due Now list shrinks)
    const pendingNextItemRef = useRef<MissedQuestionReviewItem | null>(null);

    // --- Seed PromptContext for a review item ---
    const loadQuestionIntoContext = useCallback((questionItem: MissedQuestionReviewItem) => {
        setReflectionInput(questionItem.userReflectionText || '');
        setQuestionText(questionItem.questionText);
        setChoices(questionItem.choices);
        setInteractionStage('questionGenerated');
        setCorrectAnswer(questionItem.correctAnswer);
        setExplanationText(questionItem.explanation);
        setSelectedSkill(questionItem.skill);
        setSelectedDifficulty(questionItem.difficulty);
        setPictureURL(questionItem.pictureURL || null);
        clearChat();
    }, [
        setQuestionText, setChoices, setInteractionStage, setCorrectAnswer, setExplanationText,
        setSelectedSkill, setSelectedDifficulty, setPictureURL, clearChat,
    ]);

    const resetInteractionState = useCallback(() => {
        setReviewMode('practice');
        setCurrentSelection('');
        setFreeResponseInput('');
        setIsPracticeSubmitted(false);
        setSelectedComfortLevel(null);
        setReflectionInput('');
    }, []);

    const clearQuestionContext = useCallback(() => {
        setQuestionText('');
        setChoices([]);
        setCorrectAnswer('');
        setExplanationText('');
        setInteractionStage('notStarted');
        setPictureURL(null);
        clearChat();
        resetInteractionState();
        seededIndexRef.current = null;
    }, [setQuestionText, setChoices, setCorrectAnswer, setExplanationText, setInteractionStage, setPictureURL, clearChat, resetInteractionState]);

    // --- Effect 1: Load missed questions from completed attempts ---
    useEffect(() => {
        if (!user) return;
        setLoading(true);
        setError(null);

        if (!actionProcessed) {
            const locationState = location.state as ReviewLocationState | null;
            if (locationState?.action && locationState?.testId) {
                initialActionDetailsRef.current = {
                    action: locationState.action,
                    testId: locationState.testId,
                    targetQuestionIndex: locationState.targetQuestionIndex,
                    targetSectionIndex: locationState.targetSectionIndex,
                };
                setInitialContextTestId(locationState.testId);
                setViewMode('recentTest');
                navigate(location.pathname, { replace: true, state: {} });
                setActionProcessed(true);
            }
        }

        const attemptsToProcess = user.testAttempts.filter(attempt => {
            if (initialContextTestId) {
                return attempt.id === initialContextTestId && attempt.completed;
            }
            return attempt.completed;
        });

        if (attemptsToProcess.length === 0) {
            setAllMissedQuestions([]);
            setLoading(false);
            return;
        }

        const processAttempts = async () => {
            try {
                const missedItems: MissedQuestionReviewItem[] = [];

                for (const attempt of attemptsToProcess) {
                    const testJsonPath = getTestJsonPath(attempt.id);
                    if (!testJsonPath) continue;

                    let testData = testDataCache.current[testJsonPath];
                    if (!testData) {
                        const response = await fetch(testJsonPath);
                        if (!response.ok) {
                            console.error(`Failed to load test data from ${testJsonPath}`);
                            continue;
                        }
                        testData = await response.json();
                        testDataCache.current[testJsonPath] = testData;
                    }

                    if (!attempt.sections?.length) continue;

                    if (testData.questions) {
                        // Diagnostic / demo: flat questions array
                        for (let sectionIndex = 0; sectionIndex < attempt.sections.length; sectionIndex++) {
                            const section = attempt.sections[sectionIndex];
                            if (!section?.questions) continue;

                            const reviewNeeded = section.questions.filter(q => !q.isCorrect || q.reviewStatus);
                            for (const question of reviewNeeded) {
                                let fullQuestion = testData.questions.find(
                                    (q: TestQuestion) => q.index === question.questionIndex
                                );
                                if (!fullQuestion && typeof question.questionIndex === 'number'
                                    && question.questionIndex >= 0 && question.questionIndex < testData.questions.length) {
                                    fullQuestion = testData.questions[question.questionIndex];
                                }
                                if (fullQuestion) {
                                    missedItems.push(buildReviewItem(question, fullQuestion, attempt, sectionIndex));
                                }
                            }
                        }
                    } else if (testData.sections) {
                        // Full test: nested sections — spread UserAnswerRecord so reviewStatus/nextReviewDate persist
                        for (let sectionIndex = 0; sectionIndex < attempt.sections.length; sectionIndex++) {
                            const section = attempt.sections[sectionIndex];
                            if (!section?.questions || !testData.sections[sectionIndex]?.questions) continue;

                            const reviewNeeded = section.questions.filter(q => !q.isCorrect || q.reviewStatus);
                            for (const question of reviewNeeded) {
                                const fullQuestion = testData.sections[sectionIndex].questions.find(
                                    (tq: TestQuestion) => tq.index === question.questionIndex
                                );
                                if (fullQuestion) {
                                    missedItems.push(buildReviewItem(question, fullQuestion, attempt, sectionIndex));
                                }
                            }
                        }
                    }
                }

                setAllMissedQuestions(missedItems);
            } catch (err) {
                console.error('Error processing test attempts:', err);
                setError('Failed to load question data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        processAttempts();
    }, [user, initialContextTestId, actionProcessed, location.key]);

    // --- Filtered list (useMemo) ---
    const filteredMissedQuestions = useMemo(() => {
        if (!Array.isArray(allMissedQuestions)) return [];

        const now = Date.now();
        let filtered: MissedQuestionReviewItem[] = [];

        if (viewMode === 'spacedRepetition') {
            // Due items OR never-reviewed incorrects (so dashboard Start Review isn't empty)
            filtered = allMissedQuestions.filter(q => {
                if (q.reviewStatus === 'Mastered! - No Review') return false;
                if (!q.reviewStatus && !q.isCorrect) return true; // never reviewed miss
                return !!(q.nextReviewDate && q.nextReviewDate <= now);
            }).sort((a, b) => {
                // Never-reviewed first, then oldest due date
                const aDate = a.nextReviewDate ?? 0;
                const bDate = b.nextReviewDate ?? 0;
                return aDate - bDate;
            });
        } else if (viewMode === 'recentTest') {
            let targetTestId = initialContextTestId;
            if (!targetTestId) {
                const completed = user?.testAttempts?.filter(t => t.completed) || [];
                if (completed.length > 0) {
                    const latest = [...completed].sort((a, b) =>
                        (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0)
                    )[0];
                    targetTestId = latest?.id ?? null;
                }
            }
            if (targetTestId) {
                filtered = allMissedQuestions
                    .filter(q => q.originalTestId === targetTestId)
                    .sort((a, b) =>
                        a.originalSectionIndex !== b.originalSectionIndex
                            ? a.originalSectionIndex - b.originalSectionIndex
                            : a.questionIndex - b.questionIndex
                    );
            }
        } else {
            // 'all' — hide mastered by default unless user wants history (kept via reviewStatus chip)
            filtered = allMissedQuestions.filter(q => q.reviewStatus !== 'Mastered! - No Review');
            if (activeFilters.testType) {
                filtered = filtered.filter(q => q.originalTestType === activeFilters.testType);
            }
            if (activeFilters.skill) {
                filtered = filtered.filter(q => q.skill === activeFilters.skill);
            }
            if (activeFilters.difficulty) {
                filtered = filtered.filter(q => q.difficulty === activeFilters.difficulty);
            }
            filtered.sort((a, b) => {
                if (a.originalTestId !== b.originalTestId) {
                    return String(a.originalTestId).localeCompare(String(b.originalTestId));
                }
                if (a.originalSectionIndex !== b.originalSectionIndex) {
                    return a.originalSectionIndex - b.originalSectionIndex;
                }
                return a.questionIndex - b.questionIndex;
            });
        }

        return filtered;
    }, [allMissedQuestions, viewMode, activeFilters, user, initialContextTestId]);

    // When switching to all/spacedRepetition after a context-only load, refetch everything
    useEffect(() => {
        if ((viewMode === 'all' || viewMode === 'spacedRepetition') && initialContextTestId !== null) {
            setLoading(true);
            setInitialContextTestId(null);
            setActionProcessed(false);
            initialActionDetailsRef.current = null;
            setCurrentQuestionIndex(null);
            seededIndexRef.current = null;
        }
    }, [viewMode, initialContextTestId]);

    // Apply deep-link action index + seed PromptContext when entering a question
    useEffect(() => {
        if (loading) return;

        if (actionProcessed && initialActionDetailsRef.current) {
            const { action, targetQuestionIndex, targetSectionIndex } = initialActionDetailsRef.current;
            let nextIndex: number | null = null;

            if (action === 'startRecentReview') {
                nextIndex = filteredMissedQuestions.length > 0 ? 0 : null;
            } else if (action === 'reviewSpecific' && targetQuestionIndex !== undefined && targetSectionIndex !== undefined) {
                const found = filteredMissedQuestions.findIndex(
                    q => q.questionIndex === targetQuestionIndex
                        && q.originalSectionIndex === targetSectionIndex
                        && q.originalTestId === initialContextTestId
                );
                nextIndex = found !== -1 ? found : (filteredMissedQuestions.length > 0 ? 0 : null);
            } else if (action === 'reviewSpecific' && targetQuestionIndex !== undefined) {
                const found = filteredMissedQuestions.findIndex(
                    q => q.questionIndex === targetQuestionIndex && q.originalTestId === initialContextTestId
                );
                nextIndex = found !== -1 ? found : (filteredMissedQuestions.length > 0 ? 0 : null);
            } else if (action === 'viewRecent') {
                nextIndex = null;
                if (viewMode !== 'recentTest') setViewMode('recentTest');
            }

            initialActionDetailsRef.current = null;

            if (nextIndex !== null && filteredMissedQuestions[nextIndex]) {
                setCurrentQuestionIndex(nextIndex);
                resetInteractionState();
                loadQuestionIntoContext(filteredMissedQuestions[nextIndex]);
                seededIndexRef.current = nextIndex;
            } else {
                setCurrentQuestionIndex(null);
            }
            return;
        }

        // Advance to pending next item after save (list may have shrunk when item left Due Now)
        if (pendingNextItemRef.current) {
            const next = pendingNextItemRef.current;
            pendingNextItemRef.current = null;
            const idx = filteredMissedQuestions.findIndex(q =>
                q.originalTestId === next.originalTestId
                && q.originalSectionIndex === next.originalSectionIndex
                && q.questionIndex === next.questionIndex
            );
            if (idx !== -1) {
                setCurrentQuestionIndex(idx);
                resetInteractionState();
                loadQuestionIntoContext(filteredMissedQuestions[idx]);
                seededIndexRef.current = idx;
            } else if (filteredMissedQuestions.length > 0) {
                setCurrentQuestionIndex(0);
                resetInteractionState();
                loadQuestionIntoContext(filteredMissedQuestions[0]);
                seededIndexRef.current = 0;
            } else {
                setCurrentQuestionIndex(null);
                clearQuestionContext();
                setShowCompletionSummary(true);
            }
            return;
        }

        // If current index is out of bounds after filter change, clear it
        if (currentQuestionIndex !== null && currentQuestionIndex >= filteredMissedQuestions.length) {
            setCurrentQuestionIndex(null);
            clearQuestionContext();
        }
    }, [filteredMissedQuestions, loading, actionProcessed, initialContextTestId, viewMode]);

    // Clear PromptContext when leaving the page
    useEffect(() => {
        return () => {
            setQuestionText('');
            setChoices([]);
            setCorrectAnswer('');
            setExplanationText('');
            setInteractionStage('notStarted');
            setPictureURL(null);
        };
    }, []);

    const availableTestTypes = useMemo(() => {
        const types = new Set(allMissedQuestions.map(q => q.originalTestType));
        return Array.from(types).sort();
    }, [allMissedQuestions]);

    const availableSkills = useMemo(() => {
        const skills = new Set(allMissedQuestions.map(q => q.skill));
        return Array.from(skills).sort();
    }, [allMissedQuestions]);

    const availableDifficulties = useMemo(() => {
        const order = ['Easy', 'Medium', 'Hard'];
        const difficulties = new Set(allMissedQuestions.map(q => q.difficulty));
        return Array.from(difficulties).sort((a, b) => order.indexOf(a) - order.indexOf(b));
    }, [allMissedQuestions]);

    // --- Handlers ---

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        setViewMode(newValue as 'spacedRepetition' | 'recentTest' | 'all');
        setCurrentQuestionIndex(null);
        setShowCompletionSummary(false);
        clearQuestionContext();
    };

    const handleFilterChange = (filterType: 'testType' | 'skill' | 'difficulty', value: string) => {
        setActiveFilters(prev => {
            const next = { ...prev };
            if (!value) delete next[filterType];
            else next[filterType] = value as any;
            return next;
        });
        setCurrentQuestionIndex(null);
        clearQuestionContext();
    };

    const handleReviewQuestionClick = (index: number) => {
        setShowCompletionSummary(false);
        setCurrentQuestionIndex(index);
        resetInteractionState();
        const questionItem = filteredMissedQuestions[index];
        if (questionItem) {
            loadQuestionIntoContext(questionItem);
            seededIndexRef.current = index;
        }
    };

    const handleBackToList = () => {
        setCurrentQuestionIndex(null);
        clearQuestionContext();
    };

    const handleStartReviewing = () => {
        if (filteredMissedQuestions.length > 0) {
            handleReviewQuestionClick(0);
        }
    };

    const handlePracticeAnswerSelect = (answer: string) => {
        if (reviewMode === 'practice' && !isPracticeSubmitted) {
            setCurrentSelection(answer);
        }
    };

    const handleSubmitPracticeAnswer = () => {
        const item = currentQuestionIndex !== null ? filteredMissedQuestions[currentQuestionIndex] : null;
        const isFreeResponse = item && (!item.choices || item.choices.length === 0);
        if (reviewMode !== 'practice') return;
        if (isFreeResponse) {
            if (!freeResponseInput.trim()) return;
            setCurrentSelection(freeResponseInput.trim());
            setIsPracticeSubmitted(true);
            setReviewMode('explanation');
            setInteractionStage('answered');
            return;
        }
        if (currentSelection) {
            setIsPracticeSubmitted(true);
            setReviewMode('explanation');
            setInteractionStage('answered');
        }
    };

    const handleSaveAndProceed = async () => {
        if (currentQuestionIndex === null) {
            setError('Could not save review status. Please try again.');
            return;
        }

        const currentItem = filteredMissedQuestions[currentQuestionIndex];
        if (!currentItem || !user?.testAttempts || !user.studyPlan) {
            setError('Could not save review status. Please try again.');
            return;
        }

        const status = selectedComfortLevel || DEFAULT_COMFORT;
        const reflectionText = reflectionInput;
        setSaving(true);

        const updatedTestAttempts = JSON.parse(JSON.stringify(user.testAttempts)) as TestAttempt[];
        let updatedStudyPlan = JSON.parse(JSON.stringify(user.studyPlan)) as StudyPlanData;

        const attemptIndex = updatedTestAttempts.findIndex(a => a.id === currentItem.originalTestId);
        if (attemptIndex === -1) {
            setError('Error finding original test data.');
            setSaving(false);
            return;
        }

        const sectionIndex = currentItem.originalSectionIndex;
        if (!updatedTestAttempts[attemptIndex].sections[sectionIndex]) {
            setError('Error finding original section data.');
            setSaving(false);
            return;
        }

        const questionIndexInRecord = updatedTestAttempts[attemptIndex].sections[sectionIndex].questions.findIndex(
            q => q.questionIndex === currentItem.questionIndex
        );
        if (questionIndexInRecord === -1) {
            setError('Error finding original question data.');
            setSaving(false);
            return;
        }

        const nextReviewDate = calculateNextReviewDate(status, Date.now());
        updatedTestAttempts[attemptIndex].sections[sectionIndex].questions[questionIndexInRecord] = {
            ...updatedTestAttempts[attemptIndex].sections[sectionIndex].questions[questionIndexInRecord],
            reviewStatus: status,
            nextReviewDate,
            userReflectionText: reflectionText,
        };

        let reviewIncremented = false;
        const originalReviewStatus = user.testAttempts[attemptIndex]
            ?.sections[sectionIndex]
            ?.questions[questionIndexInRecord]?.reviewStatus;

        if (!originalReviewStatus) {
            if (updatedStudyPlan.overallQuestionReviewGoal) {
                updatedStudyPlan.overallQuestionReviewGoal.current =
                    (updatedStudyPlan.overallQuestionReviewGoal.current || 0) + 1;
                reviewIncremented = true;
            }

            const today = new Date();
            const currentWeek = getCurrentWeek(updatedStudyPlan, today);
            if (currentWeek?.weeklyReviewMissedQuestionGoal) {
                const weekIndex = currentWeek.weekNumber - 1;
                if (updatedStudyPlan.weeklyPlans[weekIndex]?.weeklyReviewMissedQuestionGoal) {
                    updatedStudyPlan.weeklyPlans[weekIndex].weeklyReviewMissedQuestionGoal.current =
                        (updatedStudyPlan.weeklyPlans[weekIndex].weeklyReviewMissedQuestionGoal.current || 0) + 1;
                    reviewIncremented = true;
                }
            }

            if (reviewIncremented) {
                const practiceCorrect = isPracticeSubmitted && (
                    currentItem.choices?.length
                        ? currentSelection === currentItem.correctAnswer
                        : freeResponseInput.trim() === currentItem.correctAnswer
                );
                const weeklyProgressUpdate: WeeklyProgressUpdate = {
                    questionsAttempted: 1,
                    questionsCorrect: practiceCorrect ? 1 : 0,
                    activityType: 'review',
                    skillUpdates: {
                        [currentItem.skill]: {
                            attempted: 1,
                            correct: practiceCorrect ? 1 : 0,
                            difficulty: currentItem.difficulty.toLowerCase() as 'easy' | 'medium' | 'hard',
                        },
                    },
                    studyMinutes: 3,
                };
                updatedStudyPlan = applyWeeklyProgressToStudyPlan(updatedStudyPlan, weeklyProgressUpdate);
            }
        }

        try {
            const updates: Partial<UserData> = { testAttempts: updatedTestAttempts };
            if (reviewIncremented) updates.studyPlan = updatedStudyPlan;

            await updateUserData(updates);

            setAllMissedQuestions(prevAll => {
                const newAll = JSON.parse(JSON.stringify(prevAll)) as MissedQuestionReviewItem[];
                const indexInAll = newAll.findIndex(q =>
                    q.originalTestId === currentItem.originalTestId
                    && q.originalSectionIndex === currentItem.originalSectionIndex
                    && q.questionIndex === currentItem.questionIndex
                );
                if (indexInAll !== -1) {
                    newAll[indexInAll].reviewStatus = status;
                    newAll[indexInAll].nextReviewDate = nextReviewDate;
                    newAll[indexInAll].userReflectionText = reflectionText;
                }
                return newAll;
            });

            setSessionReviewedCount(c => c + 1);

            const nextItem = currentQuestionIndex < filteredMissedQuestions.length - 1
                ? filteredMissedQuestions[currentQuestionIndex + 1]
                : null;

            if (nextItem) {
                // Resolve index after filtered list recomputes (item may leave Due Now queue)
                pendingNextItemRef.current = nextItem;
            } else {
                setCurrentQuestionIndex(null);
                clearQuestionContext();
                setShowCompletionSummary(true);
            }
        } catch (err) {
            console.error('Failed to update user data:', err);
            setError('Failed to save review status. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex !== null && currentQuestionIndex > 0) {
            handleReviewQuestionClick(currentQuestionIndex - 1);
        }
    };

    const handlePracticeSimilar = (skill: SkillName, difficulty: 'Easy' | 'Medium' | 'Hard') => {
        navigate(`/skill-practice?skill=${encodeURIComponent(skill)}&difficulty=${difficulty}`);
    };

    // --- Render helpers ---

    const renderFilterControls = () => (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                    <InputLabel id="test-type-filter-label">Test Type</InputLabel>
                    <Select
                        labelId="test-type-filter-label"
                        value={activeFilters.testType || ''}
                        label="Test Type"
                        onChange={(e: { target: { value: string } }) => handleFilterChange('testType', e.target.value)}
                    >
                        <MenuItem value="">All Test Types</MenuItem>
                        {availableTestTypes.map(type => (
                            <MenuItem key={type} value={type}>
                                {type === 'diagnostic' ? 'Diagnostic Test' : 'Full Practice Test'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                    <InputLabel id="skill-filter-label">Skill</InputLabel>
                    <Select
                        labelId="skill-filter-label"
                        value={activeFilters.skill || ''}
                        label="Skill"
                        onChange={(e: { target: { value: string } }) => handleFilterChange('skill', e.target.value)}
                    >
                        <MenuItem value="">All Skills</MenuItem>
                        {availableSkills.map(skill => (
                            <MenuItem key={skill} value={skill}>{skill}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                    <InputLabel id="difficulty-filter-label">Difficulty</InputLabel>
                    <Select
                        labelId="difficulty-filter-label"
                        value={activeFilters.difficulty || ''}
                        label="Difficulty"
                        onChange={(e: { target: { value: string } }) => handleFilterChange('difficulty', e.target.value)}
                    >
                        <MenuItem value="">All Difficulties</MenuItem>
                        {availableDifficulties.map(difficulty => (
                            <MenuItem key={difficulty} value={difficulty}>{difficulty}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Paper>
    );

    const renderEmptyState = () => {
        if (viewMode === 'spacedRepetition') {
            return (
                <Alert severity="info" sx={{ m: 2 }}
                    action={
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button color="inherit" size="small" onClick={() => setViewMode('recentTest')}>
                                Recent Test
                            </Button>
                            <Button color="inherit" size="small" onClick={() => navigate('/skill-practice')}>
                                Practice a Skill
                            </Button>
                        </Box>
                    }
                >
                    No questions currently due for review. Great job! Browse your recent test misses or practice a skill.
                </Alert>
            );
        }
        if (viewMode === 'recentTest' && !user?.testAttempts?.some(t => t.completed)) {
            return (
                <Alert severity="info" sx={{ m: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => navigate('/diagnostic-test')}>
                            Take Diagnostic
                        </Button>
                    }
                >
                    You haven&apos;t completed any tests yet.
                </Alert>
            );
        }
        if (viewMode === 'recentTest') {
            return (
                <Alert severity="info" sx={{ m: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setViewMode('all')}>
                            Browse All
                        </Button>
                    }
                >
                    No missed questions found in your most recent test.
                </Alert>
            );
        }
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                No questions match the current filters.
            </Alert>
        );
    };

    const renderCompletionSummary = () => (
        <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, m: 2, textAlign: 'center', borderRadius: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'custom.green', mb: 1 }} />
            <Typography variant="h5" gutterBottom>Review session complete!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                You reviewed {sessionReviewedCount} question{sessionReviewedCount === 1 ? '' : 's'} this session.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={() => { setShowCompletionSummary(false); setSessionReviewedCount(0); }}>
                    Back to List
                </Button>
                <Button variant="outlined" startIcon={<SchoolIcon />} onClick={() => navigate('/skill-practice')}>
                    Practice a Skill
                </Button>
                <Button variant="outlined" onClick={() => navigate('/sat')}>
                    Dashboard
                </Button>
            </Box>
        </Paper>
    );

    const renderChoiceButtons = (item: MissedQuestionReviewItem, interactive: boolean) => {
        const softerGreenBg = 'rgba(76, 175, 80, 0.15)';
        const softerGreenBorder = '#4CAF50';
        const softerRedBg = 'rgba(244, 67, 54, 0.15)';
        const softerRedBorder = '#F44336';
        const displayedSelection = isPracticeSubmitted ? currentSelection : item.selectedAnswer;

        return (
            <Stack spacing={1.5}>
                {item.choices.map((choice, index) => {
                    const choiceLetter = String.fromCharCode(65 + index);
                    const isSelected = interactive
                        ? currentSelection === choiceLetter
                        : displayedSelection === choiceLetter;
                    const isCorrectAnswer = item.correctAnswer === choiceLetter;
                    const showGreen = !interactive && isCorrectAnswer;
                    const showRed = !interactive && isSelected && !isCorrectAnswer;
                    const showOrange = interactive && isSelected && !isPracticeSubmitted;

                    return (
                        <Button
                            key={index}
                            fullWidth
                            variant="outlined"
                            onClick={() => interactive && handlePracticeAnswerSelect(choiceLetter)}
                            disabled={!interactive || isPracticeSubmitted}
                            sx={{
                                p: 1.5, borderRadius: 2, border: 2, minHeight: 48,
                                justifyContent: 'flex-start', textTransform: 'none', transition: 'all 0.2s ease',
                                borderColor: showGreen ? softerGreenBorder
                                    : showRed ? softerRedBorder
                                    : showOrange ? 'primary.main'
                                    : 'divider',
                                bgcolor: showGreen ? softerGreenBg
                                    : showRed ? softerRedBg
                                    : showOrange ? 'custom.veryLightOrange'
                                    : 'background.paper',
                                color: 'text.primary',
                                cursor: (!interactive || isPracticeSubmitted) ? 'default' : 'pointer',
                                '&:hover': {
                                    borderColor: showGreen ? softerGreenBorder
                                        : showRed ? softerRedBorder
                                        : showOrange ? 'primary.main'
                                        : 'grey.400',
                                    bgcolor: showGreen ? softerGreenBg
                                        : showRed ? softerRedBg
                                        : showOrange ? 'custom.veryLightOrange'
                                        : 'rgba(0, 0, 0, 0.04)',
                                    color: 'text.primary',
                                    transform: (!interactive || isPracticeSubmitted) ? 'none' : 'scale(1.01)',
                                    boxShadow: (!interactive || isPracticeSubmitted) ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                },
                                '&.Mui-disabled': {
                                    borderColor: showGreen ? softerGreenBorder : showRed ? softerRedBorder : 'action.disabledBackground',
                                    bgcolor: showGreen ? softerGreenBg : showRed ? softerRedBg : 'action.disabledBackground',
                                    color: 'text.primary',
                                    opacity: 0.85,
                                },
                            }}
                            startIcon={
                                !interactive
                                    ? (showGreen ? <CheckCircleOutlineIcon sx={{ color: softerGreenBorder }} />
                                        : showRed ? <ErrorOutlineIcon sx={{ color: softerRedBorder }} />
                                        : <Box sx={{ width: 24 }} />)
                                    : undefined
                            }
                        >
                            {renderLatexOrText(choice, 'normal', true)}
                        </Button>
                    );
                })}
            </Stack>
        );
    };

    const renderActiveQuestion = () => {
        if (currentQuestionIndex === null || currentQuestionIndex >= filteredMissedQuestions.length) {
            return null;
        }
        const item = filteredMissedQuestions[currentQuestionIndex];
        const isFreeResponse = !item.choices || item.choices.length === 0;
        const progressValue = ((currentQuestionIndex + 1) / filteredMissedQuestions.length) * 100;
        const canSubmit = isFreeResponse ? freeResponseInput.trim().length > 0 : !!currentSelection;

        return (
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                {/* Progress bar — SkillPractice parity */}
                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                            height: 10, borderRadius: 5, backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundColor: 'custom.green' },
                        }}
                    />
                </Box>

                {/* Header: Exit | Skill | Question count */}
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 3 },
                }}>
                    <Box sx={{ flex: '0 0 auto' }}>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBackToList}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                '&:hover': { bgcolor: theme.palette.primary.dark },
                                borderRadius: 10, width: { xs: '95px', sm: 'auto' },
                            }}
                        >
                            <Typography variant="body1" sx={{
                                color: 'white', wordWrap: 'break-word',
                                whiteSpace: { xs: 'pre-wrap', sm: 'normal' },
                                lineHeight: { xs: 1.2, sm: 1.5 },
                            }}>
                                Back to List
                            </Typography>
                        </Button>
                    </Box>
                    <Box sx={{ flex: '1 1 auto' }}>
                        <Typography variant="h5" sx={{ textAlign: 'center' }}>{item.skill}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                label={item.difficulty}
                                size="small"
                                sx={{
                                    bgcolor: 'background.paper', color: 'text.primary', fontWeight: 'medium',
                                    border: 1, borderColor: 'primary.main',
                                }}
                            />
                            <Chip
                                label={item.originalTestType === 'diagnostic'
                                    ? 'Diagnostic'
                                    : `Full Test ${item.originalTestNumber || ''}`}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                    <Box sx={{ flex: '0 1 auto', maxWidth: { xs: '100px', sm: '200px' } }}>
                        <Typography variant="h6" sx={{ textAlign: 'center', wordWrap: 'break-word' }}>
                            Question {currentQuestionIndex + 1} of {filteredMissedQuestions.length}
                        </Typography>
                    </Box>
                </Box>

                {/* Mode toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant={reviewMode === 'practice' ? 'contained' : 'outlined'}
                            startIcon={<EditNoteIcon />}
                            disabled={isPracticeSubmitted}
                            onClick={() => setReviewMode('practice')}
                        >
                            Practice
                        </Button>
                        <Button
                            size="small"
                            variant={reviewMode === 'explanation' ? 'contained' : 'outlined'}
                            startIcon={<VisibilityIcon />}
                            onClick={() => setReviewMode('explanation')}
                        >
                            Explanation
                        </Button>
                    </Box>
                </Box>

                <Paper elevation={1} sx={{ p: { xs: 2, md: 3 }, mb: 2, bgcolor: theme.palette.background.default }}>
                    {/* Two-column question / answers */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 }, mb: 3 }}>
                        <Box sx={{ flex: { md: 1 }, minWidth: { md: '50%' } }}>
                            {item.pictureURL && (
                                <Box sx={{ mb: 2, textAlign: 'center' }}>
                                    <img
                                        src={item.pictureURL}
                                        alt={`Diagram for question ${currentQuestionIndex + 1}`}
                                        style={{
                                            maxWidth: '100%', height: 'auto', display: 'block',
                                            margin: '0 auto', borderRadius: '4px',
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                </Box>
                            )}
                            {renderLatexOrText(item.questionText)}
                        </Box>

                        <Box sx={{ flex: { md: 1 }, minWidth: { md: '45%' } }}>
                            {reviewMode === 'practice' && (
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {isFreeResponse ? 'Enter your answer:' : 'Select your answer:'}
                                    </Typography>
                                    {isFreeResponse ? (
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Enter your answer..."
                                            value={freeResponseInput}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFreeResponseInput(e.target.value)}
                                            disabled={isPracticeSubmitted}
                                            sx={{ mb: 1 }}
                                        />
                                    ) : (
                                        renderChoiceButtons(item, true)
                                    )}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmitPracticeAnswer}
                                        disabled={!canSubmit || isPracticeSubmitted}
                                        sx={{ mt: 2 }}
                                        endIcon={<CheckIcon />}
                                    >
                                        Submit Answer
                                    </Button>
                                </Stack>
                            )}

                            {reviewMode === 'explanation' && (
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1" gutterBottom>Answers:</Typography>
                                    {isFreeResponse ? (
                                        <Box>
                                            <Alert severity={
                                                isPracticeSubmitted && freeResponseInput.trim() === item.correctAnswer
                                                    ? 'success' : 'info'
                                            } sx={{ mb: 1 }}>
                                                Correct answer: {item.correctAnswer}
                                            </Alert>
                                            {(isPracticeSubmitted ? freeResponseInput : item.selectedAnswer) && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Your answer: {isPracticeSubmitted ? freeResponseInput : item.selectedAnswer}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        renderChoiceButtons(item, false)
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Box>

                    {/* Prev only — Save & Next is the single forward CTA below */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, mb: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<SkipPreviousIcon />}
                            onClick={handlePreviousQuestion}
                            disabled={currentQuestionIndex === 0}
                        >
                            Previous Question
                        </Button>
                        <Box />
                    </Box>

                    {/* Explanation → Chat → Comfort → Notes → Save (SkillPractice learning order) */}
                    {reviewMode === 'explanation' && (
                        <Box>
                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                                Explanation
                            </Typography>
                            <Paper variant="outlined" sx={{
                                p: 2, mb: 3, bgcolor: 'background.paper',
                                border: 1, borderColor: 'primary.main',
                            }}>
                                {renderLatexOrText(item.explanation)}
                            </Paper>

                            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
                                Talk with the AI to figure out how to get this right next time
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                <ChatComponent isMissedQuestionReview={true} />
                            </Box>

                            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'medium' }}>
                                    How well do you understand this now?
                                </FormLabel>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    We&apos;ll show this again based on your rating.
                                </Typography>
                                <RadioGroup
                                    name="comfort-level-group"
                                    value={selectedComfortLevel || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedComfortLevel(e.target.value as ReviewStatus)}
                                    sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}
                                >
                                    {COMFORT_OPTIONS.map((opt) => (
                                        <FormControlLabel
                                            key={opt.value}
                                            value={opt.value}
                                            control={<Radio />}
                                            label={
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">{opt.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{opt.subtitle}</Typography>
                                                </Box>
                                            }
                                            sx={{
                                                flexGrow: 1, minWidth: 140,
                                                border: 1,
                                                borderColor: selectedComfortLevel === opt.value ? 'primary.main' : 'divider',
                                                borderRadius: 1, m: 0, px: 1.5, py: 0.5,
                                                bgcolor: selectedComfortLevel === opt.value
                                                    ? theme.palette.action.selected : 'transparent',
                                                '&:hover': { bgcolor: theme.palette.action.hover },
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>

                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                label="Optional notes: What did I learn?"
                                value={reflectionInput}
                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setReflectionInput(e.target.value)}
                                sx={{ mb: 3 }}
                            />
                        </Box>
                    )}
                </Paper>

                {/* Bottom actions: Practice Similar + single Save & Next CTA */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ReplayIcon />}
                        onClick={() => handlePracticeSimilar(item.skill, item.difficulty)}
                    >
                        Practice Similar
                    </Button>

                    {reviewMode === 'explanation' ? (
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<SkipNextIcon />}
                            onClick={handleSaveAndProceed}
                            disabled={saving}
                        >
                            {saving
                                ? <CircularProgress size={24} color="inherit" />
                                : (currentQuestionIndex === filteredMissedQuestions.length - 1
                                    ? 'Finish Reviewing'
                                    : 'Save and Next')}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            onClick={() => setReviewMode('explanation')}
                        >
                            Skip to Explanation
                        </Button>
                    )}
                </Box>
            </Box>
        );
    };

    const renderQuestionList = () => (
        <Box>
            {filteredMissedQuestions.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 1 }}>
                    <Button variant="contained" onClick={handleStartReviewing} endIcon={<SkipNextIcon />}>
                        Start Reviewing ({filteredMissedQuestions.length})
                    </Button>
                </Box>
            )}
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 2, p: 2,
            }}>
                {filteredMissedQuestions.map((item, index) => (
                    <Box
                        key={`${item.originalTestId}-${item.originalSectionIndex}-${item.questionIndex}`}
                        sx={{ flex: '1 1 280px', maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 11px)' } }}
                    >
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardHeader
                                title={`#${index + 1}`}
                                subheader={`${item.skill} (${item.difficulty})`}
                                titleTypographyProps={{ variant: 'subtitle1' }}
                                subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                sx={{ pb: 0 }}
                            />
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{
                                    mb: 1, maxHeight: 100, overflow: 'hidden',
                                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                                }}>
                                    {renderLatexOrText(item.questionText, 'small')}
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    From: {item.originalTestType === 'diagnostic'
                                        ? 'Diagnostic'
                                        : `Full Test ${item.originalTestNumber || ''}`}
                                </Typography>
                                {item.reviewStatus ? (
                                    <Chip
                                        label={shortComfortLabel(item.reviewStatus)}
                                        size="small"
                                        color={
                                            item.reviewStatus === 'Hard - Review Next Time' ? 'error'
                                                : item.reviewStatus === 'Somewhat Hard - Review in 3 Days' ? 'warning'
                                                : item.reviewStatus === 'Good - Review in 14 Days' ? 'info'
                                                : 'success'
                                        }
                                        sx={{ mt: 1, fontSize: '0.75rem' }}
                                    />
                                ) : (
                                    <Chip label="Never reviewed" size="small" variant="outlined" sx={{ mt: 1, fontSize: '0.75rem' }} />
                                )}
                                {viewMode === 'spacedRepetition' && item.nextReviewDate && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'info.main' }}>
                                        Due: {new Date(item.nextReviewDate).toLocaleDateString()}
                                    </Typography>
                                )}
                            </CardContent>
                            <Box sx={{ p: 1, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button size="small" variant="contained" onClick={() => handleReviewQuestionClick(index)}>
                                    Review
                                </Button>
                            </Box>
                        </Card>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    // --- Main render ---

    if (!user) {
        return (
            <Box>
                <PersonalizedBar />
                <Alert severity="warning" sx={{ m: 3 }}>Please log in to review missed questions.</Alert>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <PersonalizedBar />
                <Alert severity="error" sx={{ m: 3 }}
                    action={<Button color="inherit" size="small" onClick={() => setError(null)}>Dismiss</Button>}
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    if (loading && allMissedQuestions.length === 0) {
        return (
            <Box>
                <PersonalizedBar />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <PersonalizedBar />
            <Box sx={{ p: 0, maxWidth: '1600px', mx: 'auto', mt: 0, width: '100%' }}>
                <Paper elevation={1} sx={{ px: { xs: 1, sm: 3 }, py: 2, mb: 1, bgcolor: theme.palette.background.default }}>
                    <Typography variant="h4" gutterBottom sx={{ px: 1 }}>Review Missed Questions</Typography>

                    {currentQuestionIndex === null && (
                        <Box sx={{
                            display: 'flex', flexDirection: 'row', alignItems: 'center',
                            width: '100%', borderBottom: 1, borderColor: 'divider', mb: 2,
                        }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
                                <Tabs
                                    value={viewMode}
                                    onChange={handleTabChange}
                                    aria-label="Review view modes"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    <Tab label="Due Now" value="spacedRepetition" icon={<EventRepeatIcon />} iconPosition="start" />
                                    <Tab label="Most Recent Test" value="recentTest" icon={<BookOpen />} iconPosition="start" />
                                    <Tab label="All Missed" value="all" icon={<DoneAllIcon />} iconPosition="start" />
                                </Tabs>
                            </Box>
                        </Box>
                    )}

                    {currentQuestionIndex === null && viewMode === 'all' && renderFilterControls()}

                    {showCompletionSummary && currentQuestionIndex === null ? (
                        renderCompletionSummary()
                    ) : currentQuestionIndex === null ? (
                        <Box>
                            {loading && <CircularProgress sx={{ display: 'block', margin: 'auto' }} />}
                            {allMissedQuestions.length === 0 && !loading && (
                                <Alert severity="info" sx={{ m: 2 }}
                                    action={
                                        <Button color="inherit" size="small" onClick={() => navigate('/skill-practice')}>
                                            Practice a Skill
                                        </Button>
                                    }
                                >
                                    You haven&apos;t missed any questions yet, or no completed tests were found.
                                </Alert>
                            )}
                            {allMissedQuestions.length > 0 && filteredMissedQuestions.length === 0 && !loading && renderEmptyState()}
                            {filteredMissedQuestions.length > 0 && !loading && renderQuestionList()}
                        </Box>
                    ) : (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentQuestionIndex}
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.3 }}
                            >
                                {renderActiveQuestion()}
                            </motion.div>
                        </AnimatePresence>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default ReviewMissedQuestionsPage;
